import type { VariantProps } from 'class-variance-authority';
import { cn, Icon } from '@lqa-frontend/ui-system';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { cva } from 'class-variance-authority';

import * as React from 'react';
import { useResolvedPortalContainer } from '../../hooks/use-portal-container';

function Dialog({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
	return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
	return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
	container,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
	const resolvedContainer = useResolvedPortalContainer(container);
	return (
		<DialogPrimitive.Portal
			data-slot="dialog-portal"
			{...props}
			container={resolvedContainer}
		/>
	);
}

function DialogClose({
	...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
	return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
	return (
		<DialogPrimitive.Overlay
			data-slot="dialog-overlay"
			className={cn(
				'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[60] bg-black/50',
				className
			)}
			{...props}
		/>
	);
}

const dialogContentVariants = cva(
	'relative max-h-[85vh] overflow-hidden bg-base-1 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 z-[70] flex flex-col w-full lg:min-w-[var(--dialog-min-width)] lg:max-w-[var(--dialog-max-width)] gap-24 rounded-m border border-outline-neutral p-24 shadow-lg duration-200',
	{
		variants: {
			/** Width scale. Bounds only apply from `lg` up; below that the dialog fills the viewport. */
			widthSize: {
				/** Confirmations, alerts, single-field prompts. */
				sm: '[--dialog-min-width:400px] [--dialog-max-width:440px]',
				/** Standard forms, settings panels. */
				md: '[--dialog-min-width:560px] [--dialog-max-width:600px]',
				/** Multi-field forms, side-by-side content. */
				lg: '[--dialog-min-width:800px] [--dialog-max-width:900px]',
				/** Data tables, complex multi-step flows. */
				xl: '[--dialog-min-width:min(1140px,90vw)] [--dialog-max-width:min(1140px,90vw)]',
			},
		},
		defaultVariants: {
			widthSize: 'md',
		},
	}
);

type DialogWidthSize = NonNullable<
	VariantProps<typeof dialogContentVariants>['widthSize']
>;

interface IDialogContentProps extends React.ComponentProps<
	typeof DialogPrimitive.Content
> {
	overlayClassName?: string;
	widthSize?: DialogWidthSize;
}

function DialogContent({
	className,
	overlayClassName,
	children,
	widthSize,
	...props
}: Readonly<IDialogContentProps>) {
	return (
		<DialogPortal data-slot="dialog-portal">
			<DialogOverlay
				className={cn(
					'fixed top-0 bottom-0 left-0 right-0 bg-black/50 grid place-items-center py-48',
					overlayClassName
				)}
			>
				<DialogPrimitive.Content
					data-slot="dialog-content"
					data-width-size={widthSize ?? 'md'}
					className={cn(dialogContentVariants({ widthSize }), className)}
					{...props}
				>
					{children}

					<DialogPrimitive.Close className="ring-offset-base-1 cursor-pointer focus:ring-base-1 data-[state=open]:bg-base-2 data-[state=open]:text-neutral absolute top-16 right-16 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-16">
						<Icon name="xLg" />
						<span className="sr-only">Close</span>
					</DialogPrimitive.Close>
				</DialogPrimitive.Content>
			</DialogOverlay>
		</DialogPortal>
	);
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-header"
			className={cn('flex flex-col gap-8 text-center sm:text-left', className)}
			{...props}
		/>
	);
}

/* Place scrollable content inside this component */
function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-body"
			className={cn('flex-1 overflow-y-auto min-h-0', className)}
			{...props}
		/>
	);
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
	return (
		<div
			data-slot="dialog-footer"
			className={cn(
				'flex flex-col-reverse gap-4 sm:flex-row sm:justify-end',
				className
			)}
			{...props}
		/>
	);
}

function DialogTitle({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
	return (
		<DialogPrimitive.Title
			data-slot="dialog-title"
			className={cn('text-20 leading-lh2 font-semibold', className)}
			{...props}
		/>
	);
}

function DialogDescription({
	className,
	...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
	return (
		<DialogPrimitive.Description
			data-slot="dialog-description"
			className={cn('text-neutral text-14', className)}
			{...props}
		/>
	);
}

export {
	Dialog,
	DialogBody,
	DialogClose,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogOverlay,
	DialogPortal,
	DialogTitle,
	DialogTrigger,
};
export type { DialogWidthSize };
